import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';

// ОСНОВА.Инженер — экран ведомости объёмов и окно исходного листа РД (выпуск 1б
// плана по замечаниям ПТО: №4 — представление, №9 — окно листа).
//
// Признаки отбора экран берёт с бэкенда — из того же отбора, что и ведомость
// материалов (XLSX): в сумме, причина исключения и строка, которая учла число,
// повтор вне суммы, возможный повтор строки сводной, графа «Сверка с листами
// РД». С выпуска 2 (замечание №4: СП01 основной, ВР не в материалах) — ещё
// статус строки, «нет в СП01», «есть в ВОР, нет в СП» с кандидатом пары и текст
// графы, как в Excel. Лист РД — картинкой с геометрией для рамок строки и шапки.

/** Строка листа РД, учтённая в строке сводной: основание графы «Сверка с
 *  листами РД». «✓» — только при равных количестве и единице. */
export interface PtoVolumeSheetCheck {
  /** Учтённая позиция (строка таблицы листа РД). */
  id: string;
  /** `dup_of` | `summary_repeat` | `summary_part`; выпуск 2 — ещё `soft_link`
   *  (та же строка по тексту ячейки или второму прочтению) и `kzh_elements`. */
  reason: string;
  same_qty: boolean;
  same_unit: boolean;
  /** Отметка этой строки в графе «Сверка с листами РД» — тем же текстом, что в
   *  Excel (выпуск 2). */
  text?: string;
}

/** Статус строки в отборе выпуска 2: `sum` — в перечне материалов, число в
 *  итоге; `repeat` — строка чертежа вне суммы (лист «Сверка» Excel); `works` —
 *  строка ВОР или позиция ИИ листа ВР (лист «Объёмы работ (ВР)»), а с причиной
 *  `drawing_work` — работа по чертежу (блок «По чертежам (не ВОР)» того же
 *  листа: отверстия и проёмы, работы ИИ, площадь окраски); `excluded` —
 *  вне суммы по общему отбору, в том числе строки, которые ведомость
 *  показывает, но в итог не берёт: «на 1 …» (`per_unit`), число — не
 *  количество (`not_quantity`), деталь применённой сборочной единицы
 *  (`assembly_applied`); показатель объекта — не материал (`not_material`). */
export type PtoVolumeStatus = 'sum' | 'repeat' | 'works' | 'excluded';

/** Признак позиции в отборе ведомости. Пустые поля сервер не присылает. */
export interface PtoVolumeMark {
  id: string;
  /** В сумме объёмов (первичная позиция отбора). */
  in_sum: boolean;
  /** Причина исключения (код) и она же по-русски. */
  reason?: string;
  reason_label?: string;
  /** Позиция, в которой число этой уже учтено. */
  counted_as?: string;
  /** Повтор вне суммы: число учтено в другой строке ревизии, решать нечего. */
  repeat?: boolean;
  /** Возможный повтор строки сводной (в сумме, на проверку) и эта строка. */
  possible_dup?: boolean;
  possible_dup_of?: string;
  /** Строки листов РД, учтённые в этой строке. */
  checks?: PtoVolumeSheetCheck[];
  /** Выпуск 2: статус строки (у сервера выпуска 1б его нет). */
  status?: PtoVolumeStatus;
  /** Строка чертежа в перечне без пары в СП01 («нет в СП01 — проверьте»). */
  unpaired?: boolean;
  /** Строка ВОР из списка «Есть в ВОР, нет в СП — проверьте». */
  vor_not_in_spec?: boolean;
  /** Текст графы «Сверка с листами РД» (отметки через «; ») или пометка строки —
   *  как в Excel. */
  sheet_check?: string;
  /** Строка «Есть в ВОР, нет в СП»: кандидат пары из СП (как в Excel) и его
   *  позиция; кандидата нет — «нет в СП». */
  pair_candidate?: string;
  pair_candidate_of?: string;
  /** ЛКМ в м² со слоями по РД («за 2 раза», «в 2 слоя»; N ≥ 2): площадь
   *  покрытия с учётом слоёв. Количество РД не меняется, в суммы не входит. */
  coating?: PtoVolumeCoating;
}

/** Площадь покрытия ЛКМ с учётом слоёв (план по замечаниям ПТО 23.10, п. 2 «а»). */
export interface PtoVolumeCoating {
  /** Слоёв по РД. */
  layers: number;
  /** Количество строки по РД. */
  area: number;
  /** Площадь × слои. */
  coverage: number;
  /** «м²», «100 м²». */
  unit: string;
  /** Слова РД о слоях: «за 2 раза». */
  phrase: string;
  /** «покрытие 186,92 м² (2 слоя)» — у количества. */
  label: string;
  /** «площадь покрытия с учётом слоёв: 93,46 × 2 = 186,92 м² (в РД — «за 2 раза»)». */
  text: string;
}

export interface PtoVolumeSelectionCounts {
  positions: number;
  in_sum: number;
  excluded: number;
  repeats: number;
  possible_dups: number;
  by_reason: Record<string, number>;
  /** Выпуск 2: строк чертежей в перечне без пары в СП01, «Есть в ВОР, нет в
   *  СП», строк ВР вне перечня. */
  unpaired?: number;
  vor_not_in_spec?: number;
  works?: number;
  /** Выпуск 2: строк листа «Ведомость материалов» Excel — ровно как в книге
   *  (строки «на 1 …» — тоже, отдельными строками вне итога); строка собирает
   *  одинаковые позиции разных листов. Нет — правила выпуска 2 выключены. */
  vedomost_rows?: number;
}

/** Признаки отбора позиций ревизии. */
export interface PtoVolumeSelection {
  revision_id: string;
  /** Правила «СП01 основной, ВР не в материалах» включены (флаг
   *  `OSNOVA_PTO_VEDOMOST_SPEC_ONLY`); нет поля — сервер выпуска 1б. */
  spec_only?: boolean;
  items: PtoVolumeMark[];
  counts: PtoVolumeSelectionCounts;
}

/** Лист РД картинкой: адрес картинки в браузере (`blob:`) и геометрия листа —
 *  JSON заголовка `X-Osnova-Sheet` как есть (разбирает окно листа). */
export interface PtoRdSheetImage {
  url: string;
  meta: string | null;
}

export interface PtoRdSheetImageArgs {
  revisionId: string;
  /** Лист PDF, с 1. */
  page: number;
  /** Файл ревизии (`raw.file`); нет — единственный файл ревизии. */
  file?: string | null;
}

/** Картинка листа — в `blob:`-адрес (в кэше RTK хранится строка, а не Blob);
 *  ошибка — общим разбором тела. */
async function sheetImageResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const meta = response.headers.get('x-osnova-sheet');
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), meta } satisfies PtoRdSheetImage;
}

export const ptoRdSheetApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Признаки отбора позиций ревизии для экрана «Позиции объёма». */
    getPtoVolumeSelection: b.query<PtoVolumeSelection, string>({
      query: (revisionId) => `/agents/pto/revisions/${revisionId}/volume-selection`,
      providesTags: ['PtoVolume'],
    }),
    /** Лист PDF ревизии картинкой (окно исходного листа РД). Лист ревизии не
     *  меняется — тегов нет; `blob:`-адрес отзывается при выходе из кэша. */
    getPtoRdSheetImage: b.query<PtoRdSheetImage, PtoRdSheetImageArgs>({
      query: ({ revisionId, page, file }) => ({
        url: `/agents/pto/revisions/${revisionId}/sheet-image`,
        params: file ? { page, file } : { page },
        responseHandler: sheetImageResponse,
      }),
      keepUnusedDataFor: 120,
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        try {
          const { data } = await cacheDataLoaded;
          await cacheEntryRemoved;
          URL.revokeObjectURL(data.url);
        } catch {
          /* запрос не удался — отзывать нечего */
        }
      },
    }),
  }),
});

export const { useGetPtoVolumeSelectionQuery, useGetPtoRdSheetImageQuery } = ptoRdSheetApi;
